import { db, getDoc, doc, collection, getDocs } from './../firebaseConfig'; // Firebase import
import { fetchAndStoreMarketData, fetchMarketData, mapIntervalToAPI } from './fetchAndStoreMarketData_v2'; // Import fetchAndStoreMarketData

export default class CustomDatafeed {
  constructor(symbol, interval, selectedInterval, startDate, endDate, sessionId, userID, startDateMillis, endDateMillis, chartWidget) {
    this.symbol = symbol;
    this.interval = interval;
    this.selectedInterval = selectedInterval;
    this.startDate = new Date(startDate).toISOString(); // Convert to UTC
    this.endDate = new Date(endDate).toISOString(); // Convert to UTC
    this.userID = userID;
    this.sessionId = sessionId;
    this.subscribers = {}; // Store subscribers for real-time updates
    this.startDateMillis = startDateMillis;
    this.endDateMillis = endDateMillis;
    this.isFetching = false;
    this.requestQueue = [];  // Queue to store pending requests
    this.barsData = []; // Initialize barsData array
    this.playbackBarsData = []; // Initialize playbackBarsData array
    this.currentIndex = 0; // Initialize currentIndex
    this.playbackCurrentIndex = 0; // Initialize playbackCurrentIndex
    this.playbackSpeed = 1; // Default playback speed
    this.currentPlaybackTime = null; // Initialize currentPlaybackTime
    this.playbackTimes = {}; // Store playback times for different intervals
    this.chartWidget = chartWidget; // Store the TradingView chart instance
    this.isInitialized = false; // Track if datafeed is initialized
    this.currentInterval = interval; // Track current interval
  }
  
  // Method to get the current playback time
  getCurrentPlaybackTime() {
    return this.currentPlaybackTime;
  }

  // Method to set the current playback time
  setCurrentPlaybackTime(time) {
    this.currentPlaybackTime = time;
    console.log('Current playback time set:', this.currentPlaybackTime);
  }

  // Method to store playback time for the current interval
  storePlaybackTime() {
    if (this.currentPlaybackTime) {
      this.playbackTimes[this.interval] = this.currentPlaybackTime;
      console.log('Playback time stored for interval:', this.interval, this.playbackTimes);
    }
  }

  // Method to retrieve playback time for a given interval
  retrievePlaybackTime(interval) {
    return this.playbackTimes[interval] || this.startDateMillis;
  }

  // Method to retrieve current playback time from Firestore
  async retrieveCurrentPlaybackTimeFromFirestore() {
    try {
      const sessionRef = doc(db, 'Users', this.userID, 'backtesting', this.sessionId);
      const sessionSnapshot = await getDoc(sessionRef);

      if (sessionSnapshot.exists()) {
        const sessionData = sessionSnapshot.data();
        console.log('Loaded session data for playback time:', sessionData);
        return sessionData.currentPlaybackTime;
      } else {
        console.error('No session found to load playback time.');
        return null;
      }
    } catch (error) {
      console.error('Error loading playback time from session:', error);
      return null;
    }
  }

  // New method to handle chunked data fetching
  async fetchAllBarsFromFirestore(interval) {
    console.log(`Fetching all bars for ${this.symbol} at interval ${interval}`);
    try {
      const intervalRef = doc(db, 'Users', this.userID, 'backtesting', this.sessionId, `intervals/${interval}`);
      const barsCollectionRef = collection(intervalRef, 'bars');
      const intervalDoc = await getDoc(intervalRef);
  
      if (!intervalDoc.exists()) {
        const intervalMillis = this.getIntervalInMillis(interval);
        const rangeMillis = this.endDateMillis - this.startDateMillis;
        const barsNeeded = Math.ceil(rangeMillis / intervalMillis) + 1000;
        const adjustedStartDateMillis = this.startDateMillis - (1000 * intervalMillis);
  
        console.log(`No data found, fetching ${barsNeeded} bars from ${new Date(adjustedStartDateMillis).toISOString()}`);
        await fetchAndStoreMarketData(
          this.userID,
          this.sessionId,
          this.symbol,
          interval,
          adjustedStartDateMillis,
          this.endDateMillis,
          barsNeeded
        );
  
        const barsSnapshot = await getDocs(barsCollectionRef);
        const bars = [];
        barsSnapshot.forEach(doc => bars.push(...doc.data().data));
        return bars.sort((a, b) => a.time - b.time);
      }
  
      const barsSnapshot = await getDocs(barsCollectionRef);
      const bars = [];
      barsSnapshot.forEach(doc => bars.push(...doc.data().data));
      return bars.sort((a, b) => a.time - b.time);
    } catch (error) {
      console.error('Error fetching bars from Firestore:', error);
      return [];
    }
  }

  async getBarsForPlayback() {
    console.log('getBarsForPlayback called');

    if (!this.currentPlaybackTime) {
      const firestorePlaybackTime = await this.retrieveCurrentPlaybackTimeFromFirestore();
      this.currentPlaybackTime = firestorePlaybackTime || this.startDateMillis;
      console.log('Set currentPlaybackTime in getBarsForPlayback:', new Date(this.currentPlaybackTime).toISOString());
    }

    this.interval = this.chartWidget?.chart().resolution() || this.interval;
    this.currentInterval = this.interval;
    console.log('Resolution for playback:', this.interval);

    try {
      const marketData = await this.fetchAllBarsFromFirestore(this.interval);
      console.log(`Retrieved ${marketData.length} bars for playback`);

      if (marketData.length === 0) {
        console.log('No market data found for playback');
        return;
      }

      const filteredMarketData = this.processMarketData(marketData, this.currentPlaybackTime, true);
      this.playbackBarsData = filteredMarketData;

      this.playbackCurrentIndex = filteredMarketData
        .reduce((lastIndex, bar, index) => (bar.time <= this.currentPlaybackTime ? index : lastIndex), -1);

      if (this.playbackCurrentIndex === -1) {
        this.playbackCurrentIndex = 0;
      } else {
        this.playbackCurrentIndex = Math.min(this.playbackCurrentIndex, filteredMarketData.length - 1);
      }

      this._lastBarTime = null;

      console.log(`Playback index initialized to: ${this.playbackCurrentIndex}, time: ${
        filteredMarketData[this.playbackCurrentIndex]?.time
          ? new Date(filteredMarketData[this.playbackCurrentIndex].time).toISOString()
          : 'none'
      }`);
    } catch (error) {
      console.error('Error in getBarsForPlayback:', error);
    }
  }

  
  async getNextCandle() {
    console.log('getNextCandle called');
  
    try {
      if (this.playbackBarsData.length === 0) {
        console.log('Initializing playback data...');
        await this.getBarsForPlayback();
      } else if (this.currentInterval !== this.interval) {
        console.log('Interval changed, re-initializing playback data...');
        this.interval = this.currentInterval; // Sync interval
        await this.getBarsForPlayback();
      }
  
      if (this.playbackBarsData.length > 0 && this.playbackCurrentIndex < this.playbackBarsData.length) {
        const nextCandle = this.playbackBarsData[this.playbackCurrentIndex];
        this.playbackCurrentIndex += 1;
        this.currentPlaybackTime = nextCandle.time;
  
        console.log(`Playback progress: ${this.playbackCurrentIndex}/${this.playbackBarsData.length} - ${new Date(nextCandle.time).toISOString()}`);
        this.notifySubscribers(nextCandle);
  
        if (this.playbackCurrentIndex >= this.playbackBarsData.length) {
          console.log('Reached end of available data');
        }
  
        return nextCandle;
      } else {
        console.log("No more candles available.");
        return null;
      }
    } catch (error) {
      console.error('Error in getNextCandle:', error);
      throw error;
    }
  }

  async getBarsForNewInterval() {
    console.log('getBarsForNewInterval called');
    try {
      this.storePlaybackTime();
  
      if (!this.currentPlaybackTime) {
        const firestorePlaybackTime = await this.retrieveCurrentPlaybackTimeFromFirestore();
        this.currentPlaybackTime = firestorePlaybackTime || this.startDateMillis;
      }
  
      const currentTimeFrame = this.currentPlaybackTime;
      this.interval = this.chartWidget?.chart().resolution() || this.interval;
      console.log('Resolution after update (new interval):', this.interval);
  
      const marketData = await this.fetchAllBarsFromFirestore(this.interval);
      console.log(`Retrieved ${marketData.length} bars for interval ${this.interval}`);
  
      if (marketData.length === 0) {
        console.log('No data available for this interval. Fetching from external source...');
        const adjustedStartDate = new Date(this.startDateMillis - (30 * 24 * 60 * 60 * 1000));
        await fetchAndStoreMarketData(
          this.userID,
          this.sessionId,
          this.symbol,
          this.interval,
          adjustedStartDate.getTime(), // Pass as millis
          this.endDateMillis,
          5000 // Explicit countBack
        );
  
        const newMarketData = await this.fetchAllBarsFromFirestore(this.interval);
        console.log(`Fetched ${newMarketData.length} bars after forced refresh`);
  
        if (newMarketData.length === 0) {
          console.error('Failed to fetch data for this interval');
          return;
        }
  
        const filteredMarketData = this.processMarketData(newMarketData, currentTimeFrame);
        if (filteredMarketData.length === 0) {
          console.error('No data available after filtering');
          return;
        }
  
        this.playbackBarsData = filteredMarketData;
        this.updatePlaybackIndex(filteredMarketData, currentTimeFrame);
        this.renderBars(filteredMarketData);
        return;
      }
  
      const filteredMarketData = this.processMarketData(marketData, currentTimeFrame);
      if (filteredMarketData.length === 0) {
        console.error('No data available after filtering existing data');
        return;
      }
  
      this.updatePlaybackIndex(filteredMarketData, currentTimeFrame);
      console.log('Rendering bars for new interval up to:', new Date(currentTimeFrame).toISOString());
      this.renderBars(filteredMarketData);
  
      console.log('Bars fetched and rendered for new interval, playback index set to:', this.playbackCurrentIndex);
    } catch (error) {
      console.error('Error in getBarsForNewInterval:', error);
    }
  }

  // Helper method to process market data
  processMarketData(marketData, currentTimeFrame, forPlayback = false) {
    return marketData
      .map(bar => {
        const barTime = typeof bar.time === 'number' && bar.time < 10000000000
          ? bar.time * 1000
          : bar.time;
        return {
          time: barTime,
          open: bar.open,
          high: bar.high,
          low: bar.low,
          close: bar.close,
          volume: bar.volume || 0,
        };
      })
      .filter(bar => 
        bar.time >= this.startDateMillis && 
        bar.time <= this.endDateMillis && 
        (forPlayback ? true : bar.time <= currentTimeFrame) // Full range for playback, up to currentTimeFrame otherwise
      )
      .sort((a, b) => a.time - b.time);
  }

  // Helper method to update playback index
  updatePlaybackIndex(filteredMarketData, currentTimeFrame) {
    this.playbackCurrentIndex = filteredMarketData
      .reduce((lastIndex, bar, index) => (bar.time <= currentTimeFrame ? index : lastIndex), -1);
  
    if (this.playbackCurrentIndex === -1) {
      this.playbackCurrentIndex = filteredMarketData.length - 1; // Fallback to last candle
    }
    if (this.playbackCurrentIndex < 0) {
      this.playbackCurrentIndex = 0; // Ensure non-negative
    }
    console.log(
      `Playback index updated to: ${this.playbackCurrentIndex}, time: ${
        filteredMarketData[this.playbackCurrentIndex]?.time
          ? new Date(filteredMarketData[this.playbackCurrentIndex].time).toISOString()
          : 'none'
      }`
    );
  }

  // Helper method to render bars
  renderBars(filteredMarketData, onlyLatest = false) {
    if (filteredMarketData.length === 0) return;
    
    console.log(`First bar: ${new Date(filteredMarketData[0].time).toISOString()}`);
    console.log(`Last bar: ${new Date(filteredMarketData[filteredMarketData.length-1].time).toISOString()}`);
    
    if (onlyLatest) {
      // During playback, only notify about the most recent candle
      const lastCandle = filteredMarketData[filteredMarketData.length - 1];
      this.notifySubscribers(lastCandle);
      console.log('Rendered single candle at:', new Date(lastCandle.time).toISOString());
    } else {
      // Initial chart load - notify about all candles
      filteredMarketData.forEach(bar => {
        this.notifySubscribers(bar);
      });
      console.log('Rendered all candles for reference');
    }
  }

  async updateInterval() {
    const newInterval = this.chartWidget?.chart().resolution() || this.interval;
    if (newInterval !== this.currentInterval) {
      this.currentInterval = newInterval;
      this.interval = newInterval;
      console.log(`Interval updated to: ${this.interval}`);
      this.isInitialized = false;
      await this.getBarsForNewInterval(); // Fetch and render for the new interval
    }
  }

  // Helper function to get the interval in milliseconds
  getIntervalInMillis(resolution = null) {
    const interval = resolution || this.interval;
    const intervals = {
      '1': 60 * 1000,
      '5': 5 * 60 * 1000,
      '15': 15 * 60 * 1000,
      '30': 30 * 60 * 1000,
      '60': 60 * 60 * 1000,
      '120': 120 * 60 * 1000,
      '240': 240 * 60 * 1000,
      '1D': 24 * 60 * 60 * 1000,
      '1W': 7 * 24 * 60 * 60 * 1000,
      '1M': 30 * 24 * 60 * 60 * 1000,
      // Add mappings for text formats
      '1min': 60 * 1000,
      '5min': 5 * 60 * 1000,
      '15min': 15 * 60 * 1000,
      '30min': 30 * 60 * 1000,
      '1h': 60 * 60 * 1000,
      '4h': 240 * 60 * 1000,
      '1day': 24 * 60 * 60 * 1000,
    };
    
    return intervals[interval] || 60 * 1000; // Default to 1 minute if not found
  }

  // Notify all subscribers when a new candle is fetched
  notifySubscribers(candle) {
    try {
      Object.values(this.subscribers).forEach(subscriber => {
        if (subscriber && typeof subscriber.onRealtimeCallback === 'function') {
          if (this._lastBarTime && candle.time < this._lastBarTime) {
            console.log('Time violation detected, skipping bar:', 
              new Date(candle.time).toISOString(), 
              'Last bar was:', new Date(this._lastBarTime).toISOString());
            return; // Skip this candle
          }
  
          subscriber.onRealtimeCallback(candle);
          this._lastBarTime = candle.time; // Update only if accepted
        }
      });
    } catch (error) {
      console.error('Error notifying subscribers:', error);
    }
  }

  // This function signals that the datafeed is ready to provide data
  onReady(callback) {
    setTimeout(() => {
      callback({
        supported_resolutions: ['1', '5', '15', '30', '60', '120', '240', '1D'],
        disable_resolution_rebuild: false, 
      });
    }, 0);
  }

  async resolveSymbol(symbol, onSymbolResolvedCallback, onResolveErrorCallback) {
    if (!symbol || typeof symbol !== 'string') {
      onResolveErrorCallback('Symbol not provided');
      return;
    }
  
    const isStock = ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'TSLA', 'NVDA', 'JPM', 'WMT'].includes(symbol);
    const formattedSymbol = isStock ? symbol : `${symbol.slice(0, 3)}/${symbol.slice(3)}`;
  
    setTimeout(() => {
      onSymbolResolvedCallback({
        name: symbol,
        ticker: formattedSymbol,
        description: symbol,
        type: isStock ? 'stock' : 'forex',
        session: isStock ? '0930-1600:1234567' : '24x7', // Stock market hours vs. 24x7 for forex
        timezone: 'Etc/UTC',
        minmov: 1,
        pricescale: isStock ? 100 : 100000, // Adjust for stock precision
        has_intraday: true,
        has_daily: true,
        has_weekly_and_monthly: true,
        intraday_multipliers: ['1', '5', '15', '30', '60', '120', '240', '1D'],
        supported_resolutions: ['1', '5', '15', '30', '60', '120', '240', '1D'],
        volume_precision: 2,
        data_status: 'streaming',
        exchange: isStock ? 'NASDAQ' : 'Forex',
      });
    }, 0);
  }

  // Get historical data for the symbol
  async getBars(symbolInfo, resolution, periodParams, onHistoryCallback, onErrorCallback = () => {}) {
    console.log('CustomDatafeed getBars called with:', { symbolInfo, resolution, periodParams });

    if (this.isFetching) {
      console.log('Already fetching data, returning...');
      onHistoryCallback([], { noData: true });
      return;
    }

    this.isFetching = true;

    try {
      this.interval = resolution;
      console.log('Fetching market data for resolution:', resolution);

      // Fetch the saved playback time from Firestore if not already set
      if (!this.currentPlaybackTime) {
        const firestorePlaybackTime = await this.retrieveCurrentPlaybackTimeFromFirestore();
        this.currentPlaybackTime = firestorePlaybackTime || this.startDateMillis;
        console.log('Initialized currentPlaybackTime:', new Date(this.currentPlaybackTime).toISOString());
      }

      const marketData = await this.fetchAllBarsFromFirestore(resolution);

      if (marketData.length === 0) {
        console.log('No bars available');
        onHistoryCallback([], { noData: true });
        return;
      }

      const allMarketData = marketData
        .map(bar => {
          const barTime = typeof bar.time === 'number' && bar.time < 10000000000
            ? bar.time * 1000
            : bar.time;
          return {
            time: barTime,
            open: bar.open,
            high: bar.high,
            low: bar.low,
            close: bar.close,
            volume: bar.volume || 0,
          };
        })
        .filter(bar => bar.time <= this.endDateMillis)
        .sort((a, b) => a.time - b.time);

      this.playbackBarsData = allMarketData.filter(bar => bar.time >= this.startDateMillis && bar.time <= this.endDateMillis);

      if (!this.isInitialized) {
        // Find the index of the bar closest to the saved currentPlaybackTime
        const playbackIndex = allMarketData.findIndex(bar => bar.time >= this.currentPlaybackTime);
        if (playbackIndex === -1) {
          console.log('No bars found after currentPlaybackTime, using last available bar');
          const lastIndex = allMarketData.length - 1;
          this.playbackCurrentIndex = lastIndex;
          this.currentPlaybackTime = allMarketData[lastIndex].time;
        } else {
          this.playbackCurrentIndex = playbackIndex;
        }

        // Calculate how many bars to show before the current playback time (e.g., 1000 bars)
        const historyBars = Math.max(0, this.playbackCurrentIndex - 1000);
        const initialDisplayData = allMarketData.slice(historyBars, this.playbackCurrentIndex + 1);

        this.isInitialized = true;

        console.log(`Initial display: ${initialDisplayData.length} bars`);
        console.log(`First bar: ${new Date(initialDisplayData[0].time).toISOString()}`);
        console.log(`Last bar: ${new Date(initialDisplayData[initialDisplayData.length - 1].time).toISOString()}`);
        onHistoryCallback(initialDisplayData, { noData: false });
      } else {
        const currentTimeFrame = this.currentPlaybackTime;
        const filteredMarketData = allMarketData.filter(bar => bar.time <= currentTimeFrame);

        this.updatePlaybackIndex(this.playbackBarsData, currentTimeFrame);

        console.log(`Displaying ${filteredMarketData.length} bars up to ${new Date(currentTimeFrame).toISOString()}`);
        onHistoryCallback(filteredMarketData, { noData: false });
      }
    } catch (error) {
      console.error('Error fetching market data:', error);
      onErrorCallback(error);
    } finally {
      this.isFetching = false;
    }
  }

  
  // Subscribe to real-time updates
  subscribeBars(symbolInfo, resolution, onRealtimeCallback, subscriberUID, onResetCacheNeededCallback) {
    console.log(`Subscribing to real-time updates for ${symbolInfo.name} at ${resolution}`);
    this.subscribers[subscriberUID] = {
      symbolInfo,
      resolution,
      onRealtimeCallback,
      onResetCacheNeededCallback,
    };
  }

  // Unsubscribe from real-time updates
  unsubscribeBars(subscriberUID) {
    console.log(`Unsubscribing from real-time updates for ${subscriberUID}`);
    delete this.subscribers[subscriberUID];
  }
}